import { useState, useEffect } from 'react';
import api from '../../../api/client';

export default function EntityTreePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', functional_currency: 'USD', parent_id: '' });

  async function load() {
    setLoading(true);
    try {
      const { data: d } = await api.get('/entities/legal-entities');
      setItems(d.data?.items || d.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.code || !form.name) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/entities/legal-entities', {
        code: form.code,
        name: form.name,
        functional_currency: form.functional_currency,
        parent_id: form.parent_id || null,
      });
      setForm({ code: '', name: '', functional_currency: 'USD', parent_id: '' });
      setShowForm(false);
      await load();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  }

  function renderTree(nodes, parentId = null, depth = 0) {
    return nodes
      .filter((n) => (n.parent_id ?? null) === parentId)
      .map((n) => (
        <li key={n.id} data-testid={`entity-row-${n.id}`} style={{ paddingLeft: depth * 16 }}>
          <span className="entity-code">{n.code}</span>
          {' — '}
          <span className="entity-name">{n.name}</span>
          {' '}
          <span className="badge">{n.functional_currency}</span>
          {n.entity_type && <span className="muted"> · {n.entity_type}</span>}
          <ul>{renderTree(nodes, n.id, depth + 1)}</ul>
        </li>
      ));
  }

  return (
    <section data-testid="entities-tree-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Entity Tree</h1>
          <p className="muted">Legal entities and their parent/child hierarchy.</p>
        </div>
        <button className="btn primary" data-testid="entity-add" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'Add Entity'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="entities-tree-error">{error}</div>}

      {showForm && (
        <form onSubmit={handleAdd} className="card form-card row-form">
          <label>Code
            <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="e.g. US-HQ" />
          </label>
          <label>Name
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. BOS Corp US" />
          </label>
          <label>Functional currency
            <input value={form.functional_currency} onChange={(e) => setForm((f) => ({ ...f, functional_currency: e.target.value }))} placeholder="USD" />
          </label>
          <label>Parent entity ID (optional)
            <input value={form.parent_id} onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))} placeholder="leave blank for root" />
          </label>
          <div className="actions">
            <button className="btn primary" type="submit" disabled={busy || !form.code || !form.name}>Save</button>
          </div>
        </form>
      )}

      <div className="card">
        <ul data-testid="entity-list" className="entity-tree-list">
          {renderTree(items)}
          {!loading && items.length === 0 && <li className="muted">No entities yet.</li>}
        </ul>
      </div>
    </section>
  );
}
